'use client';

import Script from 'next/script';
import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useUser } from '@clerk/nextjs';
import {
  trackCompleteRegistration,
  hasMetaRegistrationSent,
  markMetaRegistrationSent,
} from '@/lib/metaPixel';

const PIXEL_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID;
const SIGN_UP_INTENT_KEY = 'meta_pixel_signup_intent';

export default function MetaPixel() {
  const pathname = usePathname();
  const { isSignedIn, user, isLoaded } = useUser();
  const hasTrackedRegistration = useRef(false);

  // /sign-up, /sign-in 페이지 방문 시 가입 의도 플래그 세팅
  useEffect(() => {
    if (pathname?.startsWith('/sign-up') || pathname?.startsWith('/sign-in')) {
      sessionStorage.setItem(SIGN_UP_INTENT_KEY, 'true');
    }
  }, [pathname]);

  // 회원가입 완료 감지 및 이벤트 전송
  useEffect(() => {
    if (!isLoaded || hasTrackedRegistration.current) return;

    if (isSignedIn && user) {
      const hadSignUpIntent = sessionStorage.getItem(SIGN_UP_INTENT_KEY) === 'true';
      const createdAt = user.createdAt;
      const createdTime = createdAt ? new Date(createdAt).getTime() : 0;
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      const isNewUser = createdTime > 0 && now - createdTime < fiveMinutes;

      if (hadSignUpIntent && isNewUser && !hasMetaRegistrationSent()) {
        trackCompleteRegistration({
          content_name: 'jeongcheogi',
          status: 'success',
        });
        markMetaRegistrationSent();
        hasTrackedRegistration.current = true;
        sessionStorage.removeItem(SIGN_UP_INTENT_KEY);

        if (process.env.NODE_ENV === 'development') {
          console.log('[Meta Pixel] Sign-up conversion sent:', user.id);
        }
      }
    }
  }, [isLoaded, isSignedIn, user]);

  if (!PIXEL_ID) {
    return null;
  }

  return (
    <>
      <Script
        id="meta-pixel"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${PIXEL_ID}');
            fbq('track', 'PageView');
          `,
        }}
      />
      <noscript>
        {/* Meta Pixel 트래킹용 1x1 pixel — noscript fallback이라 next/image 사용 불가 */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          height="1"
          width="1"
          style={{ display: 'none' }}
          src={`https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`}
          alt=""
        />
      </noscript>
    </>
  );
}
